import * as Sentry from '@sentry/nextjs';

Sentry.init({
  enabled: process.env.NEXT_PUBLIC_LOGGING_ENABLED === 'true',
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  tracesSampleRate: 0,
  release: process.env.NEXT_PUBLIC_GIT_SHA,
  environment: process.env.NEXT_PUBLIC_APP_ENV,
  denyUrls: [
    /googletagmanager\.com/i,
    /analytics\.tiktok\.com/i,
    /webkit-masked-url/i,
    /ocuboost/i,
    /spotify/i,
    /cdn-cgi/i,
    /yimg/i,

    // third-party pixels
    /ocurate\.com/i,
    /tiktok/i,
    /sc-static\.net/i,
    /adsrvr/i,
    /pdst\.fm/i,
    /obtp\.js/,

    // I am not really sure what injects scripts from this domain; could be a CF worker, but it
    // is very noisy.
    /cloudflareinsights\.com/i,

    // this is a consent manager used on some sites, sometimes the api calls to it that browsers make barf
    /privacyedge-lokker/i,

    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /safari-web-extension/i,
  ],
  beforeSend(event, hint) {
    const error_message = getMessageFromException(hint.originalException);

    // denyUrls seems unreliable because the error thrown from the last frame is not always the actual file where the error occurred, but sometimes
    // where it is *caught*. See https://github.com/getsentry/sentry-javascript/issues/3045#issuecomment-726450247
    const ignoreUrlPatterns = [
      // third-party pixels
      /ocuboost\.com/i,
      /ocurate\.com/i,
      /tiktok/i,

      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ];

    const values = event?.exception?.values || [];
    const frames = values[0]?.stacktrace?.frames || [];
    const filesFromIgnoredUrls = [];
    frames.forEach((frame) => {
      const sourceFile = frame.filename;
      if (
        ignoreUrlPatterns.some(
          (pattern) => sourceFile && pattern.test(sourceFile),
        )
      ) {
        filesFromIgnoredUrls.push(sourceFile);
      }
    });

    if (filesFromIgnoredUrls.length > 0) {
      return null;
    }

    const ignoredErrorPatterns = [
      /loading chunk \d* failed./i,
      /Minified React error./i,
      /ChunkLoadError/i,
      /Hydration failed because the initial UI does not match what was rendered on the server/i,
      /There was an error while hydrating/i,
      /Non-Error exception captured/i,
      /Non-Error promise rejection captured/i,
      /\(type=unhandledrejection\) captured as promise rejection/i,
      /couldn't find the correct component chunk/i,
      // issue with some chromium browsers, likely a bug in them, not our code
      /SecurityError: Failed to execute 'sendBeacon'/i,
      /Operation is insecure/i,
      // https://developers.facebook.com/community/threads/320013549791141/
      /AutofillCallbackHandler/i,
      /AutoFillPopupClose/i,

      // This is from tiktok/instagram browsers that are trying to measure the web performance of pages that get
      // clicked through to via an ad. Whatever 3p lib they are using sometimes breaks on some phones with a TypeError
      /_perf.domInteractive/i,

      // Facebook marketing pixel related
      /fbq/,

      // Network Errors are usually just because, well, that is how the internet works.
      // User could be in an elevator, the desert, using an ad blocker that cuts off the network to 3p code, etc,
      // but many 3p libs do not properly capture them. Also, archiving in Sentry doesn't always work
      // because of how Sentry groups in Next.js apps; it seems to have trouble recognizing errors as the same.
      /Failed to fetch/i,
      /NetworkError/i,
      /Network Error/i,
      /Load Failed/i,
    ];

    if (
      ignoredErrorPatterns.some(
        (pattern) => error_message && pattern.test(error_message),
      )
    ) {
      return null;
    }

    return event;
  },
});

const getMessageFromException = (e) => {
  if (typeof e === 'string') {
    return e;
  }

  if (e !== null && typeof e === 'object') {
    return e.message ? e.message : '';
  }

  // fall through
  return '';
};
